export type RecurrenceFrequency = 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY';

export const FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  YEARLY: 'Anual',
};

/** Intervalo em meses entre dois lançamentos (igual ao backend) */
export const FREQUENCY_PLURAL_LABELS: Record<RecurrenceFrequency, string> = {
  MONTHLY: 'Mensais',
  QUARTERLY: 'Trimestrais',
  SEMIANNUAL: 'Semestrais',
  YEARLY: 'Anuais',
};

/** Cor do selo de cada periodicidade (fundo + texto, claro e escuro) */
export const FREQUENCY_BADGE_CLASSES: Record<RecurrenceFrequency, string> = {
  MONTHLY: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  QUARTERLY: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  SEMIANNUAL: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  YEARLY: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

export const FREQUENCY_INTERVAL_MONTHS: Record<RecurrenceFrequency, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMIANNUAL: 6,
  YEARLY: 12,
};

export const FREQUENCY_OPTIONS = (Object.keys(FREQUENCY_LABELS) as RecurrenceFrequency[]).map((value) => ({
  value,
  label: FREQUENCY_LABELS[value],
}));

const MONTH_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "dia 5" (mensal) ou "todo 15/mar" / "a cada 3 meses, dia 10" */
export function describeSchedule(frequency: RecurrenceFrequency, dayOfMonth: number, startDate: string): string {
  if (frequency === 'MONTHLY') return `Todo dia ${dayOfMonth}`;
  const month = Number(startDate.slice(5, 7));
  if (frequency === 'YEARLY') return `Todo ${dayOfMonth}/${MONTH_SHORT[month - 1]}`;
  return `A cada ${FREQUENCY_INTERVAL_MONTHS[frequency]} meses, dia ${dayOfMonth}`;
}

/**
 * Próximas datas (DD/MM/AAAA) depois da data inicial (YYYY-MM-DD), com a mesma regra do backend:
 * se o mês não tiver o dia, usa o último dia do mês.
 */
export function nextOccurrences(
  startDate: string,
  frequency: RecurrenceFrequency,
  dayOfMonth: number,
  count: number
): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !dayOfMonth) return [];
  const [year, month] = startDate.split('-').map(Number);
  const interval = FREQUENCY_INTERVAL_MONTHS[frequency];

  return Array.from({ length: count }, (_, i) => {
    const target = new Date(Date.UTC(year, month - 1 + (i + 1) * interval, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    const day = Math.min(dayOfMonth, lastDay);
    return `${String(day).padStart(2, '0')}/${String(target.getUTCMonth() + 1).padStart(2, '0')}/${target.getUTCFullYear()}`;
  });
}

/** YYYY-MM-DD → DD/MM/AAAA */
export function formatIsoDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}
